import { type MutableRefObject,useCallback } from 'react'

import type { ComicPageGenerateRequestBody } from '../../api/comicEpisodeJobs'
import { createComicPageGenerateTask } from '../../api/comicEpisodeJobs'
import { persistComicPageImageUrls } from '../../api/comics'
import { isApiError } from '../../api/errors'
import type { ClientUiPublic } from '../../api/platformConfig'
import type { ToastShowOptions } from '../../context/ToastContext'
import { getEpisodeSettings } from '../../lib/comicEpisodeSettings'
import { appendPageCandidates } from '../../lib/comicPageCandidates'
import { pageGenerationReconcileKey } from '../../lib/comicPageGenerationRecovery'
import { buildPageGenerationSnapshot } from '../../lib/comicPageGenerationSnapshot'
import { shouldRequirePageSelection } from '../../lib/comicPageImageStatus'
import {
  formatPagePanelCapacity,
  pagePanelCapacity,
} from '../../lib/comicPageLayout'
import { pageHasGeneratableScript } from '../../lib/comicPagePrompt'
import {
  resolveComicModelReferenceCap,
  validatePageReferencesBeforeGenerate,
} from '../../lib/comicPageReferenceCap'
import { getPanelsOnPage } from '../../lib/comicPages'
import {
  getActiveEpisode,
  setProjectCoverIfEmpty,
  updatePageInEpisode,
} from '../../lib/comicProjectMutations'
import {
  type ComicProjectSettings,
  getProjectPanelAspect,
  validateComicProjectSettings,
  validateComicProjectSettingsForPanelAspect,
} from '../../lib/comicProjectSettings'
import type { ComicProject } from '../../lib/comicTypes'
import { waitForCreationTask } from '../../lib/creationTaskLifecycle'

type UseComicPageGenerationOptions = {
  comicModelsReady: boolean
  clientUi: ClientUiPublic
  applyProject: (next: ComicProject, opts?: { save?: boolean }) => void
  persistProject: (next: ComicProject, quiet?: boolean) => Promise<void>
  showToast: (options: ToastShowOptions) => string
  pageGenerationInFlightRef: MutableRefObject<Set<string>>
}

export function useComicPageGeneration({
  comicModelsReady,
  clientUi,
  applyProject,
  persistProject,
  showToast,
  pageGenerationInFlightRef,
}: UseComicPageGenerationOptions) {
  const generatePageForEpisode = useCallback(
    async (
      baseProject: ComicProject,
      episodeId: string,
      pageId: string,
      settingsOverride?: ComicProjectSettings,
      options?: {
        quietToast?: boolean
        generateRequest?: ComicPageGenerateRequestBody
        successMessage?: string
      },
    ): Promise<{ project: ComicProject | null; needsSelection: boolean }> => {
      const ep = getActiveEpisode(baseProject, episodeId)
      const page = ep.pages.find((p) => p.id === pageId)
      if (!page) return { project: null, needsSelection: false }
      const pagePanels = getPanelsOnPage(ep, page)
      const cap = pagePanelCapacity(page)
      if (cap.overflow > 0) {
        if (!options?.quietToast) {
          showToast({
            message: `${page.title} 分镜 ${formatPagePanelCapacity(page)} 超出版式容量，请新建分页或调整分镜后再生图`,
            variant: 'warning',
          })
        }
        return { project: null, needsSelection: false }
      }
      if (!pageHasGeneratableScript(pagePanels)) {
        if (!options?.quietToast) {
          showToast({ message: `${page.title}：请先填写画面描述`, variant: 'warning' })
        }
        return { project: null, needsSelection: false }
      }
      if (!comicModelsReady || clientUi.imageModels.length === 0) {
        showToast({ message: '模型配置未就绪', variant: 'error' })
        return { project: null, needsSelection: false }
      }
      const panelAspect = getProjectPanelAspect(baseProject)
      if (!panelAspect) {
        showToast({ message: '画册单页比例未配置，请先在画册设置中选择比例', variant: 'warning' })
        return { project: baseProject, needsSelection: false }
      }

      const s =
        settingsOverride ??
        getEpisodeSettings(baseProject, ep, clientUi) ??
        baseProject.settings
      const settingsCheck = validateComicProjectSettings(s, clientUi)
      if (!settingsCheck.ok) {
        if (!options?.quietToast) {
          showToast({ message: settingsCheck.message, variant: 'warning' })
        }
        return { project: baseProject, needsSelection: false }
      }
      const pairCheck = validateComicProjectSettingsForPanelAspect(s, clientUi, panelAspect)
      if (!pairCheck.ok) {
        if (!options?.quietToast) {
          showToast({ message: pairCheck.message, variant: 'warning' })
        }
        return { project: baseProject, needsSelection: false }
      }
      const inFlightKey = pageGenerationReconcileKey(episodeId, pageId)
      pageGenerationInFlightRef.current.add(inFlightKey)
      let working = baseProject

      try {
        const refCap = resolveComicModelReferenceCap(clientUi, s.platformId, s.modelId)
        const refValidate = validatePageReferencesBeforeGenerate(
          baseProject.creative,
          pagePanels,
          refCap,
        )
        if (!refValidate.ok) {
          if (!options?.quietToast) {
            showToast({ message: refValidate.message, variant: 'warning' })
          }
          return { project: baseProject, needsSelection: false }
        }
        const created = await createComicPageGenerateTask(
          baseProject.id,
          episodeId,
          pageId,
          options?.generateRequest,
        )
        working = updatePageInEpisode(baseProject, episodeId, pageId, {
          pageImageStatus: 'generating',
          pageErrorMessage: undefined,
          pageCreationTaskId: created.publicId,
        })
        applyProject(working)

        const task = await waitForCreationTask(created)
        const rawUrls = (task.result?.imageUrls ?? []).filter(Boolean)
        if (task.status !== 'SUCCEEDED' || rawUrls.length === 0) {
          const msg = task.errorMessage ?? '整页生图未完成'
          working = updatePageInEpisode(working, episodeId, pageId, {
            pageImageStatus: 'failed',
            pageErrorMessage: msg,
          })
          applyProject(working, { save: true })
          if (!options?.quietToast) showToast({ message: msg, variant: 'error' })
          return { project: working, needsSelection: false }
        }
        const urls = await persistComicPageImageUrls(baseProject.id, rawUrls, page.title, {
          episodeId,
          pageId,
        })
        working = appendPageCandidates(
          working,
          episodeId,
          pageId,
          urls,
          created.publicId,
          s.maxCandidatesStored,
          s.candidatesPerGeneration,
          buildPageGenerationSnapshot(s, panelAspect),
        )
        const needsSelection = shouldRequirePageSelection(s.candidatesPerGeneration)
        const appliedUrl =
          working.episodes
            .find((e) => e.id === episodeId)
            ?.pages.find((p) => p.id === pageId)?.pageImageUrl ?? urls[0]
        if (!baseProject.coverUrl && appliedUrl) {
          working = setProjectCoverIfEmpty(working, appliedUrl)
        }
        await persistProject(working, true)
        if (!options?.quietToast) {
          showToast({
            message: needsSelection
              ? `${page.title}：已生成 ${urls.length} 张，请选择一张应用`
              : (options?.successMessage ?? `${page.title} 整页已生成`),
            variant: needsSelection ? 'warning' : 'success',
            autoCloseMs: needsSelection ? 0 : 4000,
          })
        }
        return { project: working, needsSelection }
      } catch (e) {
        const msg = isApiError(e) ? e.message : e instanceof Error ? e.message : '整页生图失败'
        working = updatePageInEpisode(working, episodeId, pageId, {
          pageImageStatus: 'failed',
          pageErrorMessage: msg,
        })
        applyProject(working, { save: true })
        if (!options?.quietToast) showToast({ message: msg, variant: 'error' })
        return { project: working, needsSelection: false }
      } finally {
        pageGenerationInFlightRef.current.delete(inFlightKey)
      }
    },
    [
      comicModelsReady,
      clientUi,
      applyProject,
      persistProject,
      showToast,
      pageGenerationInFlightRef,
    ],
  )

  return { generatePageForEpisode }
}
